import React, {
  type FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { createSearchParams, useNavigate } from "react-router-dom";

import { faWeightScale } from "@fortawesome/free-solid-svg-icons";
import { useFormik } from "formik";

import { User } from "@/app/auth/auth";
import { NutritionGoals } from "@/app/nutrition-wizard/nutrition-goals";
import { NutritionObjectives } from "@/app/nutrition-wizard/nutrition-objectives";
import {
  aestethicNutritionGoalOptions,
  aestethicNutritionTypeOptions,
  bodyCompositionOptions,
  calculateKcal,
  cuisineOptions,
  definitionNutritionTypeOptions,
  healthNutritionGoalOptions,
  healthNutritionTypeOptions,
  hypertrophyNutritionTypeOptions,
  objectiveOptions,
  trainingDurationOptions,
  workEffortOptions,
} from "@/app/nutrition-wizard/nutrition-wizard";
import {
  useFinishNutritionWizardMutation,
  useLazyMealOptionsQuery,
} from "@/app/nutrition-wizard/nutrition-wizard.api";
import {
  nutritionWizardInitialValues,
  nutritionWizardValidationSchema,
  NutritionWizardValues,
} from "@/app/nutrition-wizard/nutrition-wizard.schema";
import { genreOptions } from "@/app/training-wizard/training-wizard";

import { Button } from "../atoms/Button";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { RadioButtonGroup } from "../atoms/RadioButtonGroup";
import { DialogCreate } from "../molecules/DialogCreate";
import { EditorType } from "@/app/nutrition/editor-type.enum";
import { BodyCompositions } from "@/app/nutrition-wizard/body-compositions";

export type NutritionWizardProps = {
  isLoading?: boolean;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  user?: User;
  personalInformation?: any;
  height?: any;
  weight?: any;
  trainingProgress?: any;
  training?: any;
  onSubmit: (values: NutritionWizardValues) => void;
  setRecommendationVisible: () => void;
};

export const NutritionWizard: FC<NutritionWizardProps> = ({
  isLoading,
  visible,
  setVisible,
  personalInformation,
  height,
  weight,
  setRecommendationVisible,
  trainingProgress,
  user,
}) => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const [finishNutritionWizard, finishNutritionWizardResult] =
    useFinishNutritionWizardMutation();
  const [mealOptions, mealOptionsResult] = useLazyMealOptionsQuery();
  const [recommendation, setRecommendation] = useState<
    BodyCompositions | undefined
  >();

  const formik = useFormik({
    initialValues: nutritionWizardInitialValues,
    validationSchema: nutritionWizardValidationSchema,
    onSubmit: (values) => {
      finishNutritionWizard({
        age: values.age,
        genre: values.genre,
        height: values.height,
        weight: values.weight,
        nutritionType: values.nutritionType,
        objective: values.objective,
        nutritionId: values.meals?.nutritionId,
        userId: user?.id,
        tableNutrition: values.meals?.tableNutrition,
        cuisine: values.cuisine,
      });
    },
  });

  useEffect(() => {
    if (personalInformation) {
      formik.setFieldValue("genre", personalInformation?.sex?.toLowerCase());
      formik.setFieldValue("age", personalInformation?.age);
    }
  }, [personalInformation]);

  useEffect(() => {
    formik.setFieldValue("height", height?.height);
    formik.setFieldValue("weight", weight?.weight);
  }, [weight, height]);

  useEffect(() => {
    if (trainingProgress) {
      formik.setFieldValue("objective", trainingProgress.trainingObjective);
    }
  }, [trainingProgress]);

  useEffect(() => {
    if (formik.values.objective) {
      formik.setFieldValue("cuisine", undefined);
      formik.setFieldValue("nutritionType", undefined);
      formik.setFieldValue("bodyComposition", undefined);
      formik.setFieldValue("workEffortLevel", undefined);
      formik.setFieldValue("trainingDuration", undefined);
    }
  }, [formik.values.objective]);

  const nutritionGoalOptions = useMemo(() => {
    if (formik.values.objective == NutritionObjectives.Health) {
      return healthNutritionGoalOptions;
    } else return aestethicNutritionGoalOptions;
  }, [formik.values.objective]);

  const handleRecommendation = useCallback(() => {
    const weightInKilograms = formik.values.weight;
    const heightInCentimeters = formik.values.height;

    const heightInMeters = heightInCentimeters / 100;
    const imc = weightInKilograms / Math.pow(heightInMeters, 2);

    if (imc > 30) setRecommendation(BodyCompositions.Obesity);
    else if (imc > 25) setRecommendation(BodyCompositions.Overweight);
    else if (imc > 18.5) setRecommendation(BodyCompositions.Normal);
    else setRecommendation(BodyCompositions.Muscular);
  }, [formik.values.height, formik.values.weight]);

  useEffect(() => {
    console.debug("formik kcal");
    console.debug(formik.values.kcal);
    if (
      user?.id &&
      formik.values.cuisine &&
      formik.values.nutritionType &&
      formik.values.kcal &&
      formik.values.objective
    ) {
      mealOptions({
        userId: user.id,
        cuisine: formik.values.cuisine,
        nutritionType: formik.values.nutritionType,
        kcal: formik.values.kcal,
        objective: formik.values.objective,
      });
    }
  }, [formik.values.kcal]);

  const meals = useMemo(() => {
    if (mealOptionsResult.data?.length == 0) return [];

    return mealOptionsResult.data?.map((meal) => ({
      value: meal,
      text: t(`contexts.nutrition.meals_${meal.numberOfMeals}`),
      description: t(
        `contexts.nutrition.meals_${meal.numberOfMeals}_description`
      ),
    }));
  }, [mealOptionsResult]);

  useEffect(() => {
    console.debug("kcal useEffect");

    console.debug(formik.values);

    if (
      formik.values.objective === NutritionObjectives.Health &&
      formik.values.nutritionGoal === NutritionGoals.OptimalSpent
    ) {
      console.debug("not kcal");
      formik.setFieldValue("kcal", undefined);
      return;
    }

    if (
      !formik.values.age ||
      !formik.values.height ||
      !formik.values.weight ||
      !formik.values.genre ||
      formik.values.bodyComposition === undefined ||
      formik.values.nutritionGoal === undefined ||
      formik.values.trainingDuration === undefined ||
      formik.values.workEffortLevel === undefined
    ) {
      console.debug("missing parameters");
      formik.setFieldValue("kcal", undefined);
      return;
    }

    formik.setFieldValue(
      "kcal",
      calculateKcal({
        age: formik.values.age,
        height: formik.values.height,
        weight: formik.values.weight,
        bodyComposition: formik.values.bodyComposition,
        genre: formik.values.genre,
        nutritionGoal: formik.values.nutritionGoal,
        trainingDuration: formik.values.trainingDuration,
        workEffortLevel: formik.values.workEffortLevel,
      })
    );
  }, [
    formik.values.age,
    formik.values.genre,
    formik.values.weight,
    formik.values.height,
    formik.values.objective,
    formik.values.nutritionGoal,
    formik.values.trainingDuration,
    formik.values.workEffortLevel,
    formik.values.bodyComposition,
  ]);

  useEffect(() => {
    if (finishNutritionWizardResult.isSuccess) {
      navigate({
        pathname: "/nutrition",
        search: createSearchParams({
          user_id: user.id,
          id: finishNutritionWizardResult.data?.id,
          type: EditorType.Wizard,
        }).toString(),
      });
    }
  }, [finishNutritionWizardResult]);

  const isHealth = useMemo(
    () => formik.values.objective === NutritionObjectives.Health,
    [formik.values.objective]
  );

  const isDefinition = useMemo(
    () => formik.values.objective === NutritionObjectives.Definition,
    [formik.values.objective]
  );

  const isHypertrophy = useMemo(
    () => formik.values.objective === NutritionObjectives.Hypertrophy,
    [formik.values.objective]
  );

  const isWeightLoss = useMemo(
    () =>
      formik.values.objective === NutritionObjectives.Health &&
      formik.values.nutritionGoal === NutritionGoals.WeightLoss,
    [formik.values.objective, formik.values.nutritionGoal]
  );

  console.debug({
    isHypertrophy,
    isDefinition,
    isHealth,
    isWeightLoss,
  });

  return (
    <Fragment>
      <DialogCreate
        isLoading={finishNutritionWizardResult.isLoading}
        tittleButtonCancel={t("general.cancel")}
        tittleButtonCreate="Crear"
        open={visible}
        OnclickCancel={() => setVisible(!visible)}
        OnclickCreate={() => {
          console.debug("onSubmit");
          formik.handleSubmit();
        }}
        acceptDisabled={!formik.isValid}
        setOpen={() => setVisible(!visible)}
        tittle={t("contexts.nutrition.wizard_title")}
        subTittle={t("contexts.nutrition.wizard_subtitle")}
      >
        <form action="">
          <div className="mt-6 mx-2">
            <div className="space-y-12">
              <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
                <div className="flex items-center justify-center h-full text-center">
                  <div>
                    <h2 className="text-base font-semibold leading-7 text-gray-900">
                      {t("contexts.nutrition.personal_information")}
                    </h2>
                  </div>
                </div>
                <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                  <div className="col-span-full">
                    <RadioButtonGroup
                      name="genre"
                      label={t("contexts.nutrition.select_genre")}
                      options={genreOptions}
                      horizontal
                      onChange={formik.handleChange}
                      value={formik.values.genre}
                      error={formik.errors.genre}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("contexts.shared.age")}
                      type="number"
                      name="age"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.age}
                      textError={formik.errors.age}
                      error={!!formik.errors.age}
                    />
                  </div>
                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("contexts.shared.weight")}
                      type="number"
                      name="weight"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.weight}
                      textError={formik.errors.weight}
                      error={!!formik.errors.weight}
                    />
                  </div>

                  <div className="col-span-full">
                    <InputWithCornerHint
                      label={t("contexts.shared.height")}
                      type="number"
                      name="height"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.height}
                      textError={formik.errors.height}
                      error={!!formik.errors.height}
                    />
                  </div>
                </div>
              </div>
              {!trainingProgress && (
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
                  <div className="flex items-center justify-center h-full text-center">
                    <div>
                      <h2 className="text-base font-semibold leading-7 text-gray-900">
                        {t("contexts.nutrition.training_objective")}
                      </h2>
                    </div>
                  </div>
                  <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                    <div className="col-span-full">
                      <RadioButtonGroup
                        name="objective"
                        label={t(
                          "contexts.nutrition.select_training_objective"
                        )}
                        options={objectiveOptions}
                        onChange={formik.handleChange}
                        value={formik.values.objective}
                        error={formik.errors.objective}
                      />
                    </div>
                  </div>
                </div>
              )}

              {isHealth && !isWeightLoss && (
                <Fragment>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("contexts.nutrition.nutrition")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <RadioButtonGroup
                          name="cuisine"
                          label={t("contexts.nutrition.select_cuisine")}
                          options={cuisineOptions}
                          onChange={formik.handleChange}
                          value={formik.values.cuisine}
                          error={formik.errors.cuisine}
                        />
                      </div>
                      <div className="col-span-full">
                        <RadioButtonGroup
                          name="nutritionGoal"
                          label={t(
                            "contexts.nutrition.select_nutritional_objective"
                          )}
                          options={nutritionGoalOptions}
                          onChange={formik.handleChange}
                          value={formik.values.nutritionGoal}
                          error={formik.errors.nutritionGoal}
                        />
                      </div>

                      {formik.values.nutritionGoal ===
                        NutritionGoals.OptimalSpent && (
                        <Fragment>
                          <div className="col-span-full">
                            <RadioButtonGroup
                              name="nutritionType"
                              label={t(
                                "contexts.nutrition.select_nutrition_type"
                              )}
                              options={healthNutritionTypeOptions}
                              onChange={formik.handleChange}
                              value={formik.values.nutritionType}
                              error={formik.errors.nutritionType}
                            />
                          </div>
                        </Fragment>
                      )}
                      {formik.values.nutritionGoal ===
                        NutritionGoals.WeightLoss && (
                        <Fragment>
                          <div className="col-span-full">
                            <RadioButtonGroup
                              name="nutritionType"
                              label={t(
                                "contexts.nutrition.select_nutrition_type"
                              )}
                              options={definitionNutritionTypeOptions}
                              onChange={formik.handleChange}
                              value={formik.values.nutritionType}
                              error={formik.errors.nutritionType}
                            />
                          </div>
                        </Fragment>
                      )}
                    </div>
                  </div>
                </Fragment>
              )}
              {(isHypertrophy || isDefinition || isWeightLoss) && (
                <Fragment>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("contexts.nutrition.nutrition")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <RadioButtonGroup
                          name="cuisine"
                          label={t("contexts.nutrition.select_cuisine")}
                          options={cuisineOptions}
                          onChange={formik.handleChange}
                          value={formik.values.cuisine}
                          error={formik.errors.cuisine}
                        />
                      </div>
                      <div className="col-span-full">
                        <RadioButtonGroup
                          name="nutritionType"
                          label={t("contexts.nutrition.select_nutrition_type")}
                          options={
                            formik.values.objective ===
                            NutritionObjectives.Definition
                              ? definitionNutritionTypeOptions
                              : hypertrophyNutritionTypeOptions
                          }
                          onChange={formik.handleChange}
                          value={formik.values.nutritionType}
                          error={formik.errors.nutritionType}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("contexts.nutrition.physical_condition")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <RadioButtonGroup
                          name="bodyComposition"
                          label={t(
                            "contexts.nutrition.select_body_composition"
                          )}
                          highlight={bodyCompositionOptions.filter(
                            (option) => option.value === recommendation
                          )}
                          options={bodyCompositionOptions}
                          onChange={formik.handleChange}
                          value={formik.values.bodyComposition}
                          error={formik.errors.bodyComposition}
                        />
                      </div>
                      <div className="col-span-full">
                        <Button
                          onClick={handleRecommendation}
                          text="Recomendar"
                          iconLeft={faWeightScale}
                          className="w-full justify-center"
                        />
                      </div>
                      <div className="col-span-full">
                        <RadioButtonGroup
                          name="workEffortLevel"
                          label={t(
                            "contexts.nutrition.select_work_effort_level"
                          )}
                          options={workEffortOptions}
                          onChange={formik.handleChange}
                          value={formik.values.workEffortLevel}
                          error={formik.errors.workEffortLevel}
                        />
                      </div>
                      <div className="col-span-full">
                        <RadioButtonGroup
                          name="trainingDuration"
                          label={t(
                            "contexts.nutrition.select_training_duration"
                          )}
                          options={trainingDurationOptions}
                          onChange={formik.handleChange}
                          value={formik.values.trainingDuration}
                          error={formik.errors.trainingDuration}
                        />
                      </div>
                      {formik.values.objective !=
                        NutritionObjectives.Health && (
                        <div className="col-span-full">
                          <RadioButtonGroup
                            name="nutritionGoal"
                            label={t(
                              "contexts.nutrition.select_nutritional_objective"
                            )}
                            options={nutritionGoalOptions}
                            onChange={formik.handleChange}
                            value={formik.values.nutritionGoal}
                            error={formik.errors.nutritionGoal}
                          />
                        </div>
                      )}

                      <div className="col-span-full">
                        <RadioButtonGroup
                          name="meals"
                          label={t("contexts.nutrition.select_meals")}
                          options={meals}
                          onChange={formik.handleChange}
                          value={formik.values.meals}
                          error={formik.errors.meals}
                          isLoading={
                            mealOptionsResult.isLoading ||
                            mealOptionsResult.isFetching
                          }
                        />
                      </div>
                    </div>
                  </div>
                </Fragment>
              )}
            </div>
          </div>
        </form>
      </DialogCreate>
    </Fragment>
  );
};
